import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2, LogOut, RefreshCw, Trash2 } from "lucide-react";
import {
  adminCsvUrl,
  adminRequisitionsCsvUrl,
  deleteAdminEntry,
  deleteAdminRequisition,
  fetchAdminDispatches,
  fetchAdminRequisitions,
  fetchAdminStats,
  fetchAdminWaitlist,
  resetAdminStats
} from "@/lib/api";
import { useSeo } from "@/hooks/use-seo";
import { DispatchPanel } from "@/components/admin/DispatchPanel";
import { TrafficPanel } from "@/components/admin/TrafficPanel";

const STORAGE_KEY = "tff_admin_token";

const TABS = [
  { id: "waitlist", label: "Signal registry" },
  { id: "requisitions", label: "Advance requisitions" },
  { id: "traffic", label: "Traffic" },
  { id: "dispatch", label: "Dispatches" }
];

export default function Admin() {
  const [tokenInput, setTokenInput] = useState("");
  const [token, setToken] = useState("");
  const [tab, setTab] = useState("waitlist");
  const [data, setData] = useState(null);
  const [reqs, setReqs] = useState(null);
  const [stats, setStats] = useState(null);
  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: "Registry admin | The Fractured Five",
    description: "Operational console. Not for public access.",
    path: "/admin",
    noindex: true
  });

  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setToken(saved);
      setTokenInput(saved);
    }
  }, []);

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const [wl, rq, st, dp] = await Promise.all([
        fetchAdminWaitlist(t),
        fetchAdminRequisitions(t),
        fetchAdminStats(t, 14).catch(() => null),
        fetchAdminDispatches(t).catch(() => null)
      ]);
      setData(wl);
      setReqs(rq);
      setStats(st);
      setDispatch(dp);
      setToken(t);
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not load registry.");
      setData(null);
      setReqs(null);
      setStats(null);
      setDispatch(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const signOut = () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setTokenInput("");
    setData(null);
    setReqs(null);
    setStats(null);
    setDispatch(null);
    setError("");
  };

  const removeSignup = async (id, email) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${email} from the registry?`)) return;
    try {
      await deleteAdminEntry(token, id);
      toast.success("Entry removed");
      load(token);
    } catch {
      toast.error("Could not remove entry");
    }
  };

  const removeReq = async (id, ref) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove requisition ${ref}?`)) return;
    try {
      await deleteAdminRequisition(token, id);
      toast.success("Requisition removed");
      load(token);
    } catch {
      toast.error("Could not remove requisition");
    }
  };

  const purgeStats = async () => {
    try {
      await resetAdminStats(token);
      toast.success("Visitor counters purged");
      load(token);
    } catch {
      toast.error("Could not purge counters");
    }
  };

  const authed = Boolean(data);
  const onWaitlist = tab === "waitlist";
  const onTraffic = tab === "traffic";
  const onDispatch = tab === "dispatch";
  const csvHref = onWaitlist
    ? adminCsvUrl(token)
    : adminRequisitionsCsvUrl(token);

  const tabBadge = (id) => {
    if (id === "waitlist") return data?.count ?? 0;
    if (id === "requisitions") return reqs?.count ?? 0;
    if (id === "dispatch") return dispatch?.registry_total ?? 0;
    return stats?.unique_visitors ?? 0;
  };

  return (
    <div className="min-h-screen bg-[hsl(220_16%_9%)]">
      <header className="border-b border-border bg-[hsl(220_18%_6%)]">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Internal
            </p>
            <h1 className="text-lg font-semibold tracking-tight">
              Directorate records
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="rounded-[8px] border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              Site
            </Link>
            {authed && (
              <button
                type="button"
                onClick={signOut}
                data-testid="admin-signout-button"
                className="inline-flex items-center gap-2 rounded-[8px] border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors duration-150 hover:text-foreground"
              >
                <LogOut className="h-3.5 w-3.5" />
                Lock
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        {!authed ? (
          <div className="panel mx-auto max-w-md p-5 sm:p-6">
            <h2 className="text-base font-semibold">Access token required</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter the admin token configured in the backend environment.
            </p>

            <form
              className="mt-5 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (tokenInput.trim()) load(tokenInput.trim());
              }}
            >
              <input
                type="password"
                data-testid="admin-token-input"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Admin token"
                autoComplete="current-password"
                className="h-11 w-full rounded-[10px] border border-border bg-[hsl(220_18%_7%)] px-3.5 text-sm text-foreground placeholder:text-[hsl(215_10%_45%)] focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="submit"
                data-testid="admin-submit-token-button"
                disabled={loading}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[10px] bg-primary px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)] disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Unlock records"
                )}
              </button>
              {error && (
                <p
                  data-testid="admin-error-message"
                  role="alert"
                  className="font-mono text-[12px] tracking-[0.02em] text-[hsl(2_80%_66%)]"
                >
                  {error}
                </p>
              )}
            </form>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`rounded-[9px] border px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors duration-150 ${
                    tab === t.id
                      ? "border-[hsl(174_72%_42%/0.55)] bg-[hsl(174_40%_40%/0.12)] text-[hsl(174_62%_66%)]"
                      : "border-border bg-[hsl(220_18%_7%)] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                  <span className="ml-2 tabular-nums">{tabBadge(t.id)}</span>
                </button>
              ))}
            </div>

            {onDispatch ? (
              <DispatchPanel
                token={token}
                data={dispatch}
                loading={loading}
                onRefresh={() => load(token)}
              />
            ) : onTraffic ? (
              <TrafficPanel
                stats={stats}
                loading={loading}
                onRefresh={() => load(token)}
                onReset={purgeStats}
              />
            ) : (
              <>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              <div className="panel p-4 sm:col-span-1">
                <p className="eyebrow">
                  {onWaitlist ? "Total signups" : "Total requisitions"}
                </p>
                <p
                  data-testid={
                    onWaitlist ? "admin-signup-count" : "admin-requisition-count"
                  }
                  className="mt-1 font-mono text-3xl tabular-nums"
                >
                  {(onWaitlist ? data.count : reqs?.count ?? 0).toLocaleString()}
                </p>
              </div>

              <div className="panel flex flex-col justify-center gap-2 p-4 sm:col-span-2 sm:flex-row sm:items-center">
                <a
                  href={csvHref}
                  data-testid="admin-download-csv-button"
                  className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] bg-primary px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </a>
                <button
                  type="button"
                  onClick={() => load(token)}
                  data-testid="admin-refresh-button"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] border border-border px-4 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground transition-colors duration-150 hover:text-foreground"
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
              </div>
            </div>

            {onWaitlist ? (
              <div className="panel mt-6 overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_2.5rem] gap-3 border-b border-border bg-[hsl(220_18%_7%)] px-4 py-2.5">
                  <span className="eyebrow">Email</span>
                  <span className="eyebrow">Joined (UTC)</span>
                  <span className="sr-only">Actions</span>
                </div>

                {data.entries.length === 0 ? (
                  <p
                    data-testid="admin-empty-state"
                    className="px-4 py-10 text-center text-sm text-muted-foreground"
                  >
                    No signups yet.
                  </p>
                ) : (
                  <ul data-testid="admin-entries-list">
                    {data.entries.map((row) => (
                      <li
                        key={row.id}
                        className="grid grid-cols-[1fr_auto_2.5rem] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0"
                      >
                        <span className="truncate font-mono text-[12px]">
                          {row.email}
                        </span>
                        <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                          {String(row.created_at).slice(0, 16).replace("T", " ")}
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove ${row.email}`}
                          onClick={() => removeSignup(row.id, row.email)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-muted-foreground transition-colors duration-150 hover:bg-[hsl(2_72%_46%/0.12)] hover:text-[hsl(2_80%_68%)]"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="mt-6 space-y-3" data-testid="admin-requisitions-list">
                {!reqs || reqs.entries.length === 0 ? (
                  <p
                    data-testid="admin-requisitions-empty"
                    className="panel px-4 py-10 text-center text-sm text-muted-foreground"
                  >
                    No advance requisitions yet. They only arrive from readers
                    who solve the Null Six puzzle.
                  </p>
                ) : (
                  reqs.entries.map((r) => (
                    <article key={r.id} className="panel overflow-hidden">
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-[hsl(220_18%_7%)] px-4 py-2.5">
                        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[hsl(32_80%_66%)]">
                          {r.reference}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {String(r.created_at).slice(0, 16).replace("T", " ")}
                          </span>
                          <button
                            type="button"
                            aria-label={`Remove ${r.reference}`}
                            onClick={() => removeReq(r.id, r.reference)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] text-muted-foreground transition-colors duration-150 hover:bg-[hsl(2_72%_46%/0.12)] hover:text-[hsl(2_80%_68%)]"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </div>

                      <div className="px-4 py-4">
                        <p className="text-sm font-medium">{r.name}</p>
                        <a
                          href={`mailto:${r.email}?subject=${encodeURIComponent(
                            `Your advance requisition ${r.reference} — The Fractured Five`
                          )}`}
                          className="font-mono text-[12px] text-[hsl(174_62%_64%)] hover:underline"
                        >
                          {r.email}
                        </a>

                        <dl className="mt-4 grid gap-2 sm:grid-cols-2">
                          {[
                            ["File", r.book],
                            ["Form", r.book_format],
                            ["Copies", r.quantity],
                            ["Ships to", r.region || "—"]
                          ].map(([k, v]) => (
                            <div
                              key={k}
                              className="flex items-baseline justify-between gap-3 border-t border-border pt-2"
                            >
                              <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                {k}
                              </dt>
                              <dd className="font-mono text-[11px] text-right">
                                {v}
                              </dd>
                            </div>
                          ))}
                        </dl>

                        {r.message && (
                          <div className="mt-4 border-t border-border pt-3">
                            <p className="eyebrow">Message</p>
                            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-[hsl(210_12%_80%)]">
                              {r.message}
                            </p>
                          </div>
                        )}
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}

            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
              CSV contains every entry for the selected record set.
            </p>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
